import React, { useState, useEffect } from 'react';
import { getAvailableVoices, setEngine, getEngine } from '../utils/ttsService';
import api from '../services/api';
import { TTS_ENGINES } from '../constants/tts';

const TTSSettingsPanel = ({ settings, onSettingsChange }) => {
    const [voices, setVoices] = useState([]);
    const [loadingVoices, setLoadingVoices] = useState(false);

    useEffect(() => {
        let isMounted = true;

        const fetchConfig = async () => {
            try {
                const response = await api.getTTSConfig();
                if (!isMounted) return;

                if (response.data && response.data.engine) {
                    setEngine(response.data.engine);
                    if (settings.engine !== response.data.engine) {
                        onSettingsChange({
                            ...settings,
                            engine: response.data.engine,
                            autoGenderSelect: response.data.auto_gender_select,
                            patientVoiceId: response.data.patient_voice_id,
                            narratorVoiceId: response.data.narrator_voice_id,
                            rate: response.data.default_rate
                        });
                    }
                }
            } catch (error) {
                console.error('Failed to fetch TTS config:', error);
            }
        };
        fetchConfig();

        return () => { isMounted = false; };
    }, []); // Initial load only

    useEffect(() => {
        let isCancelled = false;

        const loadEngineVoices = async () => {
            setLoadingVoices(true);
            const engine = settings.engine || getEngine();
            const v = await getAvailableVoices(engine);

            if (!isCancelled) {
                setVoices(v);
                setLoadingVoices(false);
            }
        };

        loadEngineVoices();

        const handleVoicesChanged = () => {
            if (!isCancelled && (settings.engine || getEngine()) === TTS_ENGINES.BROWSER) {
                setVoices(window.speechSynthesis.getVoices());
            }
        };

        if (typeof window !== 'undefined' && window.speechSynthesis) {
            window.speechSynthesis.addEventListener('voiceschanged', handleVoicesChanged);
        }

        return () => {
            isCancelled = true;
            if (typeof window !== 'undefined' && window.speechSynthesis) {
                window.speechSynthesis.removeEventListener('voiceschanged', handleVoicesChanged);
            }
        };
    }, [settings.engine]);

    const handleChange = (key, value) => {
        const newSettings = { ...settings, [key]: value };
        if (key === 'engine') {
            setEngine(value);
        }
        onSettingsChange(newSettings);
        localStorage.setItem('gp-simulator-tts-settings', JSON.stringify(newSettings));
    };

    return (
        <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '12px',
            fontSize: '14px',
            padding: '8px 16px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            border: '1px solid #dee2e6'
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span role="img" aria-label="speaker">🔊</span>
                <label style={{ fontWeight: 600 }}>Mode:</label>
                <select
                    value={settings.mode}
                    onChange={e => handleChange('mode', e.target.value)}
                    style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        border: '1px solid #ced4da',
                        outline: 'none'
                    }}
                >
                    <option value="off">Off</option>
                    <option value="verbal">Verbal Only</option>
                    <option value="all">Verbal + Narration</option>
                </select>
            </div>

            {settings.mode !== 'off' && (
                <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <label style={{ fontWeight: 600 }}>Engine:</label>
                        <select
                            value={settings.engine || TTS_ENGINES.BROWSER}
                            onChange={e => handleChange('engine', e.target.value)}
                            style={{
                                padding: '4px 8px',
                                borderRadius: '4px',
                                border: '1px solid #ced4da',
                                outline: 'none'
                            }}
                        >
                            <option value={TTS_ENGINES.BROWSER}>Browser</option>
                            <option value={TTS_ENGINES.EDGE}>Edge (Server)</option>
                            <option value={TTS_ENGINES.PIPER}>Piper (Server)</option>
                        </select>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <label style={{ fontSize: '12px', color: '#6c757d' }}>Patient Voice:</label>
                            <select
                                value={settings.patientVoiceURI || settings.patientVoiceId || ''}
                                onChange={e => handleChange(settings.engine === TTS_ENGINES.BROWSER ? 'patientVoiceURI' : 'patientVoiceId', e.target.value)}
                                style={{
                                    padding: '2px 4px',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    maxWidth: '150px'
                                }}
                                disabled={loadingVoices}
                            >
                                <option value="">Auto-select</option>
                                {voices.map(v => (
                                    <option key={v.voiceURI || v.id} value={v.voiceURI || v.id}>
                                        {v.name} ({v.lang || v.locale})
                                    </option>
                                ))}
                            </select>
                        </div>

                        {settings.mode === 'all' && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <label style={{ fontSize: '12px', color: '#6c757d' }}>Narrator Voice:</label>
                                <select
                                    value={settings.narratorVoiceURI || settings.narratorVoiceId || ''}
                                    onChange={e => handleChange(settings.engine === TTS_ENGINES.BROWSER ? 'narratorVoiceURI' : 'narratorVoiceId', e.target.value)}
                                    style={{
                                        padding: '2px 4px',
                                        borderRadius: '4px',
                                        fontSize: '12px',
                                        maxWidth: '150px'
                                    }}
                                    disabled={loadingVoices}
                                >
                                    <option value="">Auto-select</option>
                                    {voices.map(v => (
                                        <option key={v.voiceURI || v.id} value={v.voiceURI || v.id}>
                                            {v.name} ({v.lang || v.locale})
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <label style={{ fontSize: '12px', color: '#6c757d' }}>Speed:</label>
                            <select
                                value={settings.rate || 1.0}
                                onChange={e => handleChange('rate', parseFloat(e.target.value))}
                                style={{
                                    padding: '2px 4px',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    width: '60px'
                                }}
                            >
                                <option value={0.75}>0.75x</option>
                                <option value={1.0}>1.0x</option>
                                <option value={1.25}>1.25x</option>
                                <option value={1.5}>1.5x</option>
                                <option value={2.0}>2.0x</option>
                            </select>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default TTSSettingsPanel;
